// Docked inspector frame (CONTRACT "Docked inspector"): a 340 px grid column
// on the wall step. The parent grid places it (grid-column 4); opening it
// changes the workspace width, nothing floats. Header 44: artwork slot, name
// 600 with a muted second line, Esc cap and close. Body scrolls; panels sit
// 8 px apart. useInspectorKey binds "]" to toggle while no text field has
// focus. When closed the frame renders nothing so the grid column collapses.
import { useEffect, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from './Button';
import { KeyCap } from './KeyCap';
import { cx } from './cx';
import { isTextInput } from './isTextInput';
import './DockedInspector.css';

export interface DockedInspectorProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly artwork?: ReactNode;
  readonly children: ReactNode;
  readonly label?: string;
  readonly className?: string;
}

export function DockedInspector({ open, onClose, title, subtitle, artwork, children, label = 'Inspector', className }: DockedInspectorProps) {
  if (!open) return null;
  return (
    <aside className={cx('insp', className)} aria-label={label} data-region="inspector">
      <div className="insp-h">
        {artwork ? <span className="insp-art">{artwork}</span> : null}
        <div className="insp-t">
          <b>{title}</b>
          {subtitle ? <small>{subtitle}</small> : null}
        </div>
        <div className="insp-end">
          <KeyCap>Esc</KeyCap>
          <Button variant="icon" size="sm" aria-label="Close inspector" icon={<X />} onClick={onClose} />
        </div>
      </div>
      <div className="insp-scroll">{children}</div>
    </aside>
  );
}

// "]" toggles the inspector anywhere, inert inside text fields.
export function useInspectorKey(onToggle: () => void, enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== ']' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextInput(event.target)) return;
      event.preventDefault();
      onToggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToggle, enabled]);
}
